import React, { useState } from 'react';
import { BarChart3 } from 'lucide-react';
import type { CFCommitteeSummary } from '../../types/campaignFinance';
import { fmtMoney } from '../../utils/campaignFinanceUtils';

interface CFFinancialSummaryProps {
  summary: CFCommitteeSummary;
  /** Caption describing data currency (period/filed dates), if known. */
  asOf?: string;
}

interface Step {
  label: string;
  /** Signed contribution to the running balance. */
  delta: number;
  /** Absolute pillar (Beginning / Ending) vs. a floating delta bar. */
  absolute?: boolean;
  color: string;
  valueColor: string;
}

const W = 740;
const COL_CENTERS = [62, 177, 292, 407, 522, 637];
const BAR_W = 64;
const TOP = 20;
const BASE = 175;

/**
 * Collapsible waterfall: Beginning + Donations + Loans received − Expenditures
 * − Loans repaid = Ending. Bars are positioned from running balances, so the
 * chart is fully data-driven (the comprehensive design's SVG was static).
 */
const CFFinancialSummary: React.FC<CFFinancialSummaryProps> = ({ summary, asOf }) => {
  const [open, setOpen] = useState(false);

  const steps: Step[] = [
    { label: 'Beginning', delta: summary.beginning_balance, absolute: true, color: 'var(--cf-axis)', valueColor: 'var(--cf-axis)' },
    { label: '+ Donations', delta: summary.total_raised, color: 'var(--cf-in)', valueColor: 'var(--cf-in-value)' },
    { label: "+ Loans rec'd", delta: summary.loans_originated, color: 'var(--cf-loan-mid)', valueColor: 'var(--cf-loan)' },
    { label: '− Expenditures', delta: -summary.total_spent, color: 'var(--cf-out)', valueColor: 'var(--cf-out-text)' },
    { label: '− Loans repaid', delta: -summary.loans_repaid, color: 'var(--cf-axis)', valueColor: 'var(--cf-axis)' },
    { label: '= Ending', delta: summary.ending_balance, absolute: true, color: 'var(--cf-out-darkest)', valueColor: 'var(--cf-out-darkest)' },
  ];

  // Running balances at each column (absolute pillars use their own value).
  let running = 0;
  const cols = steps.map((s) => {
    if (s.absolute) {
      const from = 0;
      const to = s.delta;
      running = s.label === '= Ending' ? to : to; // both pillars are absolute snapshots
      return { ...s, from, to, displayValue: to };
    }
    const from = running;
    const to = running + s.delta;
    running = to;
    return { ...s, from, to, displayValue: s.delta };
  });

  const allVals = cols.flatMap(c => [c.from, c.to]).concat(0);
  const maxV = Math.max(...allVals);
  const minV = Math.min(...allVals);
  const span = maxV - minV || 1;
  const yOf = (v: number) => BASE - ((v - minV) / span) * (BASE - TOP);

  const burnPct = summary.burn_rate != null ? Math.round(summary.burn_rate * 100) : null;

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-3.5 flex-wrap">
        <div className="flex items-baseline gap-3.5 flex-wrap">
          <span className="font-semibold text-base text-text-primary dark:text-text-dark-primary">Financial Summary</span>
          <span className="text-[12.5px] text-text-secondary dark:text-text-dark-secondary">
            Ending balance <strong className="tabular-nums text-text-primary dark:text-text-dark-primary">{fmtMoney(summary.ending_balance, { compact: true })}</strong>
            {' · '}raised <span className="tabular-nums text-cf-in-value">{fmtMoney(summary.total_raised, { compact: true })}</span>
            {' · '}spent <span className="tabular-nums text-primary-700 dark:text-primary-dark-400">{fmtMoney(summary.total_spent, { compact: true })}</span>
            {burnPct != null && <> · {burnPct}% burn</>}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1.5 border-[1.5px] border-border-strong dark:border-border-dark rounded-[9px] px-3 py-1.5 text-xs font-semibold text-primary-700 dark:text-primary-dark-400 bg-white dark:bg-background-dark-secondary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary transition-colors"
        >
          <BarChart3 className="w-3.5 h-3.5" />
          {open ? 'Hide chart' : 'View chart'}
        </button>
      </div>

      {open && (
        <div className="mt-3.5 pt-3.5 border-t border-border-light dark:border-border-dark">
          {asOf && <div className="text-[11px] text-accent-400 mb-1">{asOf}</div>}
          <svg width="100%" viewBox={`0 0 ${W} 210`} className="block max-w-full">
            <line x1="20" y1={BASE} x2={W - 10} y2={BASE} stroke="var(--cf-surface)" strokeWidth="1" />
            {cols.map((c, i) => {
              const cx = COL_CENTERS[i];
              const x = cx - BAR_W / 2;
              const yHigh = yOf(Math.max(c.from, c.to));
              const yLow = yOf(Math.min(c.from, c.to));
              const h = Math.max(yLow - yHigh, 3); // min 3px so zero-deltas render as a marker
              const labelY = yHigh - 6;
              const signed = c.absolute
                ? fmtMoney(c.displayValue, { compact: true })
                : `${c.displayValue >= 0 ? '+' : '−'}${fmtMoney(Math.abs(c.displayValue), { compact: true })}`;
              return (
                <g key={c.label}>
                  <rect x={x} y={yHigh} width={BAR_W} height={h} rx="2" fill={c.color} fillOpacity={c.absolute ? 1 : 0.85} />
                  <text x={cx} y={labelY} textAnchor="middle" fontSize="10.5" fontWeight="700" fill={c.valueColor}>{signed}</text>
                  <text x={cx} y="192" textAnchor="middle" fontSize="9.5" fontWeight={c.absolute ? 700 : 400} fill={c.absolute && c.label === '= Ending' ? 'var(--cf-out-darkest)' : 'var(--cf-muted)'}>{c.label}</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
};

export default CFFinancialSummary;
